import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Banknote, CheckCircle2, FileText, Hourglass, Printer, Send } from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { buttonVariants } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { FeatureStatCard } from '@/components/common/StatCard';
import { cn } from '@/lib/utils';
import { formatTaka } from '@/utils/number';
import { PCMP_PERMISSION } from '../api/pcmpAccess.api';
import {
  downloadPcmpPendingReportXlsx,
  pcmpPendingReportQueryOptions,
} from '../api/pcmpReports.api';
import { PcmpPendingReportTable } from '../components/PcmpReports/PcmpPendingReportTable';
import { PcmpExportButton } from '../components/PcmpQueue/PcmpExportButton';
import { PcmpReportFilterBar } from '../components/PcmpReports/PcmpReportFilterBar';
import { PcmpCodeSelect } from '../components/PcmpQueue/PcmpCodeSelect';
import { PCMP_PENDING_STAGE_OPTIONS } from '../constants/pcmpReports.constants';
import { usePcmpPendingReportFilters } from '../hooks/usePcmpPendingReportFilters';
import { usePcmpPermissions } from '../hooks/usePcmpPermissions';
import type { PcmpPendingStage } from '../types/pcmpReports.types';

/** Pending proposals by division and region (LDS-568, the legacy "PCMP Dashboard"). */
export function PcmpPendingReportPage() {
  const controller = usePcmpPendingReportFilters();
  const { params, stage, setStage, appliedFilters } = controller;
  const { can, isLoading: permissionsLoading } = usePcmpPermissions();
  // Every viewer may open it, scoped by the API (LDS-819): those who see every
  // proposal get the whole org, everyone else their own and their downline's.
  const seesAll = permissionsLoading || can(PCMP_PERMISSION.VIEW_ALL);
  const canExport = can(PCMP_PERMISSION.VIEW, PCMP_PERMISSION.EXPORT);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpPendingReportQueryOptions(params),
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Pending Report</h1>
          <p className="text-sm text-muted-foreground">
            {seesAll
              ? 'Proposals by division and region, and where they are waiting.'
              : "Your and your downline's proposals by division and region, and where they are waiting."}
          </p>
        </div>
        {canExport ? (
          <div className="flex flex-wrap items-center gap-2">
            <PcmpExportButton
              disabled={!data}
              download={(date) => downloadPcmpPendingReportXlsx(params, date)}
            />
            <Link
              to="/pcmp/reports/pending/print"
              search={{ ...appliedFilters, stage }}
              target="_blank"
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
            >
              <Printer className="size-3.5" />
              Print / PDF
            </Link>
          </div>
        ) : null}
      </div>

      <PcmpReportFilterBar controller={controller} idPrefix="pcmp-pending">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pcmp-pending-stage">Waiting at</Label>
          <PcmpCodeSelect<PcmpPendingStage>
            id="pcmp-pending-stage"
            options={PCMP_PENDING_STAGE_OPTIONS}
            value={stage}
            onChange={setStage}
          />
        </div>
      </PcmpReportFilterBar>

      {appliedFilters.projectId !== undefined ? (
        <p className="text-xs text-muted-foreground">
          Only projects open on the web today are counted, so a closed project shows nothing here.
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        <FeatureStatCard
          icon={FileText}
          accent="blue"
          label="Submitted"
          value={data?.cards.submitted}
          isLoading={isLoading}
          isError={!!error}
        />
        <FeatureStatCard
          icon={Hourglass}
          accent="amber"
          label="Pending"
          value={data?.cards.pending}
          isLoading={isLoading}
          isError={!!error}
        />
        <FeatureStatCard
          icon={CheckCircle2}
          accent="emerald"
          label="Verified"
          value={data?.cards.verified}
          isLoading={isLoading}
          isError={!!error}
        />
        <FeatureStatCard
          icon={Banknote}
          accent="violet"
          label="Disbursed"
          value={data?.cards.disbursed}
          isLoading={isLoading}
          isError={!!error}
        />
        <FeatureStatCard
          icon={Send}
          accent="cyan"
          label="Awaiting Publish"
          value={data?.cards.awaitingPublish}
          isLoading={isLoading}
          isError={!!error}
        />
      </div>

      {error && !data ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the Pending report"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <div className="flex flex-col gap-2 rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className="font-medium">{data?.projectLabel ?? 'Loading…'}</span>
            {data ? (
              <span className="text-muted-foreground">
                Total proposed {formatTaka(data.totalAmount)}
              </span>
            ) : null}
          </div>
          {data ? (
            <div
              className={cn('overflow-x-auto', isFetching && 'opacity-60 transition-opacity')}
              aria-busy={isFetching}
            >
              <PcmpPendingReportTable report={data} />
            </div>
          ) : (
            <div className="h-48 animate-pulse rounded-md bg-muted" aria-hidden />
          )}
        </div>
      )}
    </div>
  );
}
